import React, { useRef, useState } from "react";
import { Upload, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

// Converts an /api/files/... relative URL into an absolute URL
export function absolutizeUrl(u) {
    if (!u) return u;
    if (u.startsWith("http://") || u.startsWith("https://") || u.startsWith("data:")) return u;
    if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
    return u;
}

/**
 * ImagePicker
 * - props:
 *    value: current image URL string
 *    onChange(url): called when value changes (either typed URL or uploaded file's URL)
 *    testidPrefix: prefix for data-testid
 *    placeholder: placeholder for URL input
 */
export default function ImagePicker({ value, onChange, testidPrefix = "img", placeholder = "https://… oppure carica un file" }) {
    const { t } = useLang();
    const [mode, setMode] = useState(value && value.startsWith("http") && !value.includes("/api/files/") ? "url" : "upload");
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith("image/")) {
            toast.error("Seleziona un'immagine (JPG, PNG, WEBP, GIF).");
            return;
        }
        if (f.size > 12 * 1024 * 1024) {
            toast.error("File troppo grande. Max 12 MB.");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/upload/image", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url);
            toast.success("Foto caricata.");
        } catch (e) {
            const msg = e.response?.data?.detail || "Upload fallito";
            toast.error(typeof msg === "string" ? msg : t("common.error"));
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    return (
        <div data-testid={`${testidPrefix}-picker`} className="space-y-2">
            <div className="flex gap-2">
                <button
                    type="button"
                    onClick={() => setMode("upload")}
                    data-testid={`${testidPrefix}-mode-upload`}
                    className={`flex-1 inline-flex items-center justify-center gap-2 text-xs uppercase tracking-wider font-bold py-2 border transition-colors ${mode === "upload" ? "bg-abnormal-acid text-black border-abnormal-acid" : "border-white/20 text-zinc-400 hover:border-white"}`}
                >
                    <Upload size={14} /> {t("common.uploadFile")}
                </button>
                <button
                    type="button"
                    onClick={() => setMode("url")}
                    data-testid={`${testidPrefix}-mode-url`}
                    className={`flex-1 inline-flex items-center justify-center gap-2 text-xs uppercase tracking-wider font-bold py-2 border transition-colors ${mode === "url" ? "bg-abnormal-acid text-black border-abnormal-acid" : "border-white/20 text-zinc-400 hover:border-white"}`}
                >
                    <Link2 size={14} /> Da URL
                </button>
            </div>

            {mode === "upload" ? (
                <div>
                    <input
                        ref={inputRef}
                        data-testid={`${testidPrefix}-file-input`}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={onFile}
                        disabled={uploading}
                        className="block w-full text-xs text-zinc-400 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-abnormal-acid file:text-white file:uppercase file:tracking-wider file:font-bold hover:file:bg-white hover:file:text-abnormal-acid"
                    />
                    {uploading && (
                        <p className="mt-2 text-xs text-zinc-400 flex items-center gap-2">
                            <Loader2 className="animate-spin" size={14} /> {t("common.uploading")}
                        </p>
                    )}
                </div>
            ) : (
                <input
                    type="url"
                    data-testid={`${testidPrefix}-url-input`}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className="w-full bg-black border border-white/10 focus:border-abnormal-acid outline-none px-3 py-2 text-sm"
                />
            )}

            {value && (
                <div className="mt-2 aspect-video bg-black border border-white/10 overflow-hidden">
                    <img src={absolutizeUrl(value)} alt="preview" className="w-full h-full object-cover" />
                </div>
            )}
        </div>
    );
}
